import React, { lazy, Suspense, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  closestCenter,
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  AlertTriangle,
  ArrowLeft,
  Bell,
  FileText,
  GripHorizontal,
  Info,
  Map as MapIcon,
  MoreHorizontal,
  Printer,
  RefreshCw,
  RotateCcw,
  Send,
  SlidersHorizontal,
  Sparkles,
} from "lucide-react";
import { getFeatures, getIndustryKey, getSchedulingConfig } from "../../../constants/uiRegistry";
import { getBoardPreset, getDispatchRegistry } from "./dispatchRegistry";
import {
  applyOptimizationPlan,
  getDispatchBoard,
  getLocations,
  optimizeWithConstraints,
} from "../../../api/schedulingApi";
import LocationFilter from "./LocationFilter";
import FreshnessChip from "./FreshnessChip";
import { getDispatchVocab, capitalize } from "./dispatchVocab";
import { familiesOn } from "./serviceFamily";
import { fetchWritebackFlags, markLegsFromBoard } from "../../../api/dispatchApi";
import "./dispatch-b.css";
import "../sched/sched-c.css";
import useIsPhone from "../../../hooks/useIsPhone";
import CalendarDropdown from "./CalendarDropdown";
import ConstraintPanel from "./ConstraintPanel";
import {
  buildApplyPayload,
  buildConstraintPayload,
  buildDaySummaries,
  buildDispatchState,
  buildRouteViewModels,
  clean,
  filterPlanItemsByWindow,
  formatCurrency,
  formatDayLabel,
  formatDateTime,
  formatHours,
  formatTime,
  getExecutionKeyCandidates,
  getVisualState,
  isAppointmentModified,
  moveAppointment,
  overlayOptimizationPlan,
} from "./utils";
import StatusIndicator from "./StatusIndicator";
import { computeRouteSchedule } from "./utils/schedule";
import QuickAddSheet from "../QuickAddSheet";
import "../RoutePlanner.css";

const todayIso = () => new Date().toISOString().slice(0, 10);

// Board presentation presets — one board, three layouts. The tenant vertical
// picks the default (dispatchRegistry.boardPreset); the toggle overrides on any
// board and persists per user per tenant. All three always shown.
const BOARD_PRESETS = [
  { key: "dense", label: "Dense", title: "Dense 30-minute grid (many short stops)" },
  { key: "blocks", label: "Blocks", title: "Duration-proportional job blocks (few long jobs)" },
  { key: "projects", label: "Projects", title: "Multi-hour/day spanning project blocks, read in AM/PM bands" },
];

// A per-drag reassignment must resolve fast. Past this, stop waiting and
// RECONCILE from the server — never leave the board on an assumed state.
const REASSIGN_TIMEOUT_MS = 12000;
// The customer window a map marker opens in place.
const CustomerPopup = lazy(() => import("../../Profile/CustomerPopup"));
// The day map, and with it the Maps JavaScript API, load only when "Day map" is
// pressed: the board itself makes no request to maps.googleapis.com.
const DispatchMap = lazy(() => import("./DispatchMap"));

const shiftIsoDate = (value, amount) => {
  const base = new Date(`${clean(value) || todayIso()}T12:00:00`);
  base.setDate(base.getDate() + Number(amount || 0));
  return base.toISOString().slice(0, 10);
};

const shiftIsoMonth = (value, amount) => {
  const base = new Date(`${clean(value) || todayIso()}T12:00:00`);
  base.setMonth(base.getMonth() + Number(amount || 0));
  return base.toISOString().slice(0, 10);
};

const countWindowDays = (startDate, endDate) => {
  const start = new Date(`${clean(startDate) || todayIso()}T12:00:00`);
  const end = new Date(`${clean(endDate) || clean(startDate) || todayIso()}T12:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
  return Math.max(0, Math.round((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000)));
};

const formatNavigationDate = (value) => {
  const parsed = new Date(`${clean(value) || todayIso()}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return "Select date";
  return parsed.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const isIsoDate = (value) => /^\d{4}-\d{2}-\d{2}$/.test(clean(value));

const toWeekWindow = (day) => {
  const anchor = new Date(`${clean(day) || todayIso()}T12:00:00`);
  const weekday = anchor.getDay();
  const diffToMonday = (weekday + 6) % 7;
  const start = new Date(anchor);
  start.setDate(anchor.getDate() - diffToMonday);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return {
    start_date: start.toISOString().slice(0, 10),
    end_date: end.toISOString().slice(0, 10),
  };
};

const readApiError = (error, fallback) =>
  clean(error?.response?.data?.detail || error?.response?.data?.error || error?.message) ||
  fallback;

const resolveViewMode = (pathname) => {
  const normalized = clean(pathname).toLowerCase();
  if (normalized.endsWith("/month")) return "month";
  if (normalized.endsWith("/range")) return "range";
  return "day";
};

const validateDispatchRequest = ({ viewMode, day, rangeStart, rangeEnd }) => {
  if (!isIsoDate(day)) {
    return "Select a valid ISO date before loading the dispatch board.";
  }
  if (viewMode === "day") {
    return "";
  }
  if (viewMode === "month") {
    return "";
  }
  if (!isIsoDate(rangeStart) || !isIsoDate(rangeEnd)) {
    return "Range view requires a valid start date and end date.";
  }
  if (rangeStart > rangeEnd) {
    return "Range start must be on or before the range end date.";
  }
  return "";
};

const validateDispatchResponse = (board) => {
  if (!board || typeof board !== "object") {
    throw new Error("Dispatch board response was empty.");
  }
  if (!Array.isArray(board.routes)) {
    throw new Error("Dispatch board response is missing routes.");
  }
  if (!Array.isArray(board.baseline_items)) {
    throw new Error("Dispatch board response is missing baseline items.");
  }
  if (!board.date_range_meta || typeof board.date_range_meta !== "object") {
    throw new Error("Dispatch board response is missing date range metadata.");
  }
  return board;
};

const toViewWindow = (viewMode, day, rangeStart, rangeEnd) => {
  const anchor = new Date(`${clean(day) || new Date().toISOString().slice(0, 10)}T12:00:00`);
  if (viewMode === "month") {
    const start = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const end = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    return {
      start_date: start.toISOString().slice(0, 10),
      end_date: end.toISOString().slice(0, 10),
    };
  }
  if (viewMode === "range") {
    const start = clean(rangeStart) || clean(day);
    const end = clean(rangeEnd) || start;
    return { start_date: start, end_date: end };
  }
  return {
    start_date: clean(day),
    end_date: clean(day),
  };
};

const DEFAULT_CONSTRAINTS = {
  maxAppointmentsPerTech: 12,
  maxDriveMinutes: 300,
  valueTarget: 1200,
  maxDueDateDeviation: "",
  balanceRoutes: true,
  priorities: {
    drive: 0.4,
    value: 0.4,
    cadence: 0.2,
  },
};

const buildExecutionMap = (report) => {
  const map = new Map();
  (report?.results || []).forEach((row) => {
    [row?.crm_appointment_id, row?.external_id, row?.fieldroutes_appointment_id, row?.item_id]
      .map(clean)
      .filter(Boolean)
      .forEach((key) => map.set(key, row));
  });
  return map;
};

const toMonthWindow = (day) => {
  const base = new Date(`${clean(day) || new Date().toISOString().slice(0, 10)}T12:00:00`);
  const start = new Date(base.getFullYear(), base.getMonth(), 1);
  const end = new Date(base.getFullYear(), base.getMonth() + 1, 0);
  return {
    monthKey: `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}`,
    label: start.toLocaleDateString([], { month: "long", year: "numeric" }),
    targetWindow: {
      start_date: start.toISOString().slice(0, 10),
      end_date: end.toISOString().slice(0, 10),
    },
  };
};

const routeKeyForDay = (technicianId, day) =>
  `${clean(technicianId) || "unassigned"}:${clean(day)}`;

const appendLocalDraftRoutes = (state, localRouteDrafts = []) => {
  if (!state || !localRouteDrafts.length) return state;
  const nextState = {
    ...state,
    routeOrder: Array.isArray(state.routeOrder) ? [...state.routeOrder] : [],
    routesById: { ...(state.routesById || {}) },
  };

  localRouteDrafts.forEach((route) => {
    const routeId = clean(route?.id);
    if (!routeId || nextState.routesById[routeId]) return;
    nextState.routesById[routeId] = route;
    nextState.routeOrder.push(routeId);
  });

  return nextState;
};

const buildRouteStatusMap = (executionReport, auditReport) => {
  const routeStatuses = new Map(
    Object.entries(auditReport?.sync_status_by_route || {}).map(([key, value]) => [clean(key), clean(value)])
  );
  (executionReport?.results || []).forEach((row) => {
    const requested = row?.requested || {};
    const previous = row?.previous || {};
    const start = clean(requested?.start || previous?.start).slice(0, 10);
    const technicianId =
      clean(requested?.technician_id || previous?.technician_id) || "unassigned";
    if (!start) return;
    const routeKey = routeKeyForDay(technicianId, start);
    const current = routeStatuses.get(routeKey);
    if (current === "drift") return;
    if (row?.outcome === "drift_detected" || row?.reconciliation_status === "drift_detected") {
      routeStatuses.set(routeKey, "drift");
      return;
    }
    if (
      row?.outcome === "partial_sync" ||
      row?.reconciliation_status === "fieldroutes_unverified"
    ) {
      routeStatuses.set(routeKey, "partial");
      return;
    }
    if (!current && row?.outcome === "applied") {
      routeStatuses.set(routeKey, "synced");
    }
  });
  return routeStatuses;
};

export default function RouteBoard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [industryKey, setIndustryKey] = useState(() => getIndustryKey("general"));
  // FieldRoutes concepts (optimizer, constraints, job pool, reminders, the FR
  // inspector, the "Live (CRM)/FieldRoutes" sync chip) must never bleed into a
  // native tenant's board — Kyle should not see a concept that only exists
  // because another tenant uses FR. FR is pest_control in practice, so gate the
  // FR-only chrome on the industry. VISUAL ONLY — no write behavior changes.
  const isFieldRoutesTenant = industryKey === "pest_control";
  const schedulingConfig = useMemo(() => getSchedulingConfig(industryKey), [industryKey]);
  const dispatchRegistry = useMemo(() => getDispatchRegistry(industryKey), [industryKey]);
  const RouteColumn = dispatchRegistry.RouteColumn;
  // Board presentation preset: auto from the tenant vertical, user-overridable and
  // persisted per tenant. dense (pest) / blocks (landscaping) / projects (trades).
  const autoBoardPreset = useMemo(() => getBoardPreset(industryKey), [industryKey]);
  const boardPresetKey = useMemo(() => {
    let tid = "";
    try { tid = String(JSON.parse(localStorage.getItem("activeTenant") || "{}").id || ""); } catch { /* ignore */ }
    return `dispatch.boardPreset.${tid || "x"}`;
  }, []);
  const [boardPresetOverride, setBoardPresetOverride] = useState(() => {
    try { return localStorage.getItem(boardPresetKey) || ""; } catch { return ""; }
  });
  const boardPreset = boardPresetOverride || autoBoardPreset;
  const setBoardPreset = useCallback((next) => {
    // Choosing the tenant's auto preset clears the override (back to default).
    const value = next === autoBoardPreset ? "" : next;
    setBoardPresetOverride(value);
    try { value ? localStorage.setItem(boardPresetKey, value) : localStorage.removeItem(boardPresetKey); } catch { /* ignore */ }
  }, [autoBoardPreset, boardPresetKey]);
  const staffLabel = schedulingConfig?.staffLabel || "Technician";
  // Industry words (routes/techs · bookings/holds/rooms · crews) — gated, not hidden.
  const vocab = useMemo(() => getDispatchVocab(industryKey), [industryKey]);
  // The board is the page. "Day map" swaps the map in full-width, with a Back;
  // a route's "Map route" opens it on that route alone.
  const [mapOpen, setMapOpen] = useState(null); // null | "all" | <routeId>
  const [legendOpen, setLegendOpen] = useState(false);
  // Phone: the board shows one route at a time (the switcher picks it).
  const isPhone = useIsPhone();
  const [phoneRouteId, setPhoneRouteId] = useState("");
  // FieldRoutes writeback: a drag is refused with "Managed in FieldRoutes" until route-assignment writeback is on.
  const [writeback, setWriteback] = useState(null);
  useEffect(() => { if (!isFieldRoutesTenant) return; let alive = true; fetchWritebackFlags().then((f) => { if (alive) setWriteback(f); }); return () => { alive = false; }; }, [isFieldRoutesTenant]);
  const [freshState, setFreshState] = useState(null);
  const [profileStop, setProfileStop] = useState(null);
  // The dispatch day is shareable/linkable via ?date= — so month-view day-click
  // and the header calendar both route to a specific day's board (not today).
  const initialDay = useMemo(() => {
    const param = new URLSearchParams(location.search).get("date");
    return isIsoDate(param) ? param : todayIso();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const viewMode = useMemo(() => resolveViewMode(location.pathname), [location.pathname]);

  const [loading, setLoading] = useState(true);
  const [applyLoading, setApplyLoading] = useState(false);
  // Per-drag native reassignment in flight (optimistic move awaiting server confirm).
  const [reassigning, setReassigning] = useState(false);
  const [optimizationLoading, setOptimizationLoading] = useState(false);
  const [error, setError] = useState("");
  const [day, setDay] = useState(initialDay);
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  const [fetchedLocations, setFetchedLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const featureLocationFilter = useMemo(
    () => Boolean(getFeatures(industryKey)?.locationFilter),
    [industryKey]
  );
  const [showJobPool, setShowJobPool] = useState(false);
  const [showReminders, setShowReminders] = useState(false);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [constraintsOpen, setConstraintsOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [dispatchState, setDispatchState] = useState(null);
  const [baselineState, setBaselineState] = useState(null);
  const [boardMeta, setBoardMeta] = useState(null);
  const [constraints, setConstraints] = useState(DEFAULT_CONSTRAINTS);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState("");
  const [selectedForApplyIds, setSelectedForApplyIds] = useState([]);
  const [executionReport, setExecutionReport] = useState(null);
  const [quickAdd, setQuickAdd] = useState(null); // {assigneeUserId, assigneeName}
  const [optimizationPlan, setOptimizationPlan] = useState(null);
  const [auditReport, setAuditReport] = useState(null);
  const [activeAppointmentId, setActiveAppointmentId] = useState("");
  const [highlightedRouteId, setHighlightedRouteId] = useState("");
  const [localRouteDrafts, setLocalRouteDrafts] = useState([]);
  const requestIdRef = useRef(0);
  const highlightTimeoutRef = useRef(null);
  const routeRefs = useRef(new Map());
  const previousViewModeRef = useRef(viewMode);
  const dayPickerRef = useRef(null);
  const loadWindow = useMemo(
    () => toViewWindow(viewMode, day, rangeStart, rangeEnd),
    [day, rangeEnd, rangeStart, viewMode]
  );
  const monthWindow = useMemo(() => toMonthWindow(day), [day]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const loadBoard = useCallback(async ({ refresh = false } = {}) => {
    const validationError = validateDispatchRequest({
      viewMode,
      day,
      rangeStart,
      rangeEnd,
    });
    if (validationError) {
      requestIdRef.current += 1;
      setLoading(false);
      setError(validationError);
      setDispatchState(null);
      setBaselineState(null);
      setBoardMeta(null);
      setSelectedAppointmentId("");
      return;
    }

    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError("");
    setDispatchState(null);
    setBaselineState(null);
    setBoardMeta(null);
    try {
      const boardRes = await getDispatchBoard({
        day,
        view: viewMode,
        start_date: loadWindow.start_date,
        end_date: loadWindow.end_date,
        include_opportunities: true,
        refresh,
      });
      if (requestId !== requestIdRef.current) return;
      const board = validateDispatchResponse(boardRes?.data || {});
      markLegsFromBoard(board); // RT's directions + refresh legs ride with day_sync
      const nextState = buildDispatchState({
        board,
        day,
        schedules: [],
        serviceTypes: [],
        schedulingConfig,
      });

      if (requestId !== requestIdRef.current) return;
      setBaselineState(nextState);
      setBoardMeta(board);
      setSelectedForApplyIds([]);

      const firstAppointmentId = nextState.routeOrder
        .flatMap((routeId) => nextState.routesById[routeId]?.stopIds || [])
        .find(Boolean);

      setSelectedAppointmentId((current) =>
        current && nextState.appointmentsById[current] ? current : firstAppointmentId || ""
      );
    } catch (nextError) {
      if (requestId !== requestIdRef.current) return;
      setError(readApiError(nextError, "Unable to load the dispatch board."));
      setDispatchState(null);
      setBaselineState(null);
      setBoardMeta(null);
      setSelectedAppointmentId("");
    } finally {
      if (requestId !== requestIdRef.current) return;
      setLoading(false);
    }
  }, [day, loadWindow.end_date, loadWindow.start_date, rangeEnd, rangeStart, schedulingConfig, viewMode]);

  useEffect(() => {
    setExecutionReport(null);
    setSelectedForApplyIds([]);
    setLocalRouteDrafts([]);
  }, [day, loadWindow.end_date, loadWindow.start_date, viewMode]);

  useEffect(() => {
    const previousViewMode = previousViewModeRef.current;
    if (
      viewMode === "range" &&
      previousViewMode !== "range" &&
      !clean(rangeStart) &&
      !clean(rangeEnd)
    ) {
      const nextWeekWindow = toWeekWindow(day);
      setRangeStart(nextWeekWindow.start_date);
      setRangeEnd(nextWeekWindow.end_date);
    }
    previousViewModeRef.current = viewMode;
  }, [day, rangeEnd, rangeStart, viewMode]);

  useEffect(() => {
    loadBoard();
  }, [loadBoard]);

  // An empty grid row (or the route's "Add job") opens the quick-add sheet at
  // that slot's time. Only CRM-user columns can carry an assignee straight
  // through; FR-tech columns quick-add into the pool (FieldRoutes owns those).
  const handleQuickAdd = useCallback((route, time) => {
    const routeId = String(route?.id || "");
    const isCrmUser = routeId.startsWith("crm-user:");
    setQuickAdd({
      assigneeUserId: isCrmUser ? routeId.replace("crm-user:", "") : null,
      assigneeName: isCrmUser ? route?.technicianName || "" : "",
      time: /^\d{2}:\d{2}$/.test(String(time || "")) ? time : "09:00",
    });
  }, []);

  useEffect(() => {
    if (!baselineState) {
      setDispatchState(null);
      return;
    }
    const visiblePlanItems = filterPlanItemsByWindow(
      optimizationPlan?.plan_items || [],
      baselineState.window || loadWindow
    );
    const nextState = optimizationPlan?.plan_items?.length
      ? overlayOptimizationPlan(baselineState, visiblePlanItems)
      : baselineState;
    setDispatchState(appendLocalDraftRoutes(nextState, localRouteDrafts));
  }, [baselineState, loadWindow, localRouteDrafts, optimizationPlan]);

  useEffect(() => {
    const updateIndustry = () => setIndustryKey(getIndustryKey("general"));
    const onStorage = (event) => {
      if (event.key === "activeTenant" || event.key === "ui_overrides") {
        updateIndustry();
      }
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("activeTenant:changed", updateIndustry);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("activeTenant:changed", updateIndustry);
    };
  }, []);

  const executionByKey = useMemo(() => buildExecutionMap(executionReport), [executionReport]);
  const routeStatusByKey = useMemo(
    () => buildRouteStatusMap(executionReport, auditReport),
    [auditReport, executionReport]
  );
  const routeDeltaByKey = useMemo(() => {
    const nextMap = new Map();
    (optimizationPlan?.route_deltas || []).forEach((row) => {
      nextMap.set(routeKeyForDay(row?.technician_id, row?.service_day), row);
    });
    return nextMap;
  }, [optimizationPlan]);
  const boardStateMeta = dispatchState || baselineState || null;
  const syncMeta = boardMeta?.sync || boardStateMeta?.sync || {};
  const appointmentFreshness = boardMeta?.appointment_freshness || {};
  // Prominent staleness warning: this day's FR appointments haven't synced in
  // hours → a dispatcher could send techs off stale routes. FR tenants only
  // (native tenants read live; freshness.is_fieldroutes is false for them).
  // The banner is for a day that was NEVER synced or a refresh that just failed —
  // a tenant with a working sync reads the chip ("Synced from FieldRoutes 12 min
  // ago") and never the old "31.8 hours ago" wall.
  const showStaleBanner = Boolean(freshState?.never || freshState?.failed);
  const dateRangeMeta = boardMeta?.date_range_meta || boardStateMeta?.dateRangeMeta || {};
  const overallStatusCounts = boardMeta?.status_counts || boardStateMeta?.statusCounts || {};
  const daySummaries = useMemo(() => buildDaySummaries(boardStateMeta), [boardStateMeta]);

  const routes = useMemo(() => {
    return buildRouteViewModels(dispatchState, constraints).map((route) => {
      const routeKey = routeKeyForDay(
        route.fieldroutesTechnicianId || route.technicianId,
        route.serviceDay || dispatchState?.day
      );
      return {
        ...route,
        routeDelta: routeDeltaByKey.get(routeKey) || null,
        syncStatus: routeStatusByKey.get(routeKey) || route.syncStatus || "",
      };
    });
  }, [constraints, dispatchState, routeDeltaByKey, routeStatusByKey]);
  const scheduledRoutes = useMemo(
    () =>
      routes.map((route) => ({
        ...route,
        stops: computeRouteSchedule(route.stops || []),
      })),
    [routes]
  );
  const deferredRoutes = useDeferredValue(scheduledRoutes);

  // Location dimension lands in another lane. Probe the endpoint when the flag is
  // on; tolerate 404/empty (treated as "no filter", never an error).
  useEffect(() => {
    if (!featureLocationFilter) {
      setFetchedLocations([]);
      return undefined;
    }
    let cancelled = false;
    getLocations()
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res?.data) ? res.data : res?.data?.results || [];
        setFetchedLocations(
          rows
            .map((r) => ({ id: clean(r.id ?? r.office_id), name: clean(r.name || r.office_name || r.label) }))
            .filter((r) => r.id)
        );
      })
      .catch(() => {
        if (!cancelled) setFetchedLocations([]);
      });
    return () => {
      cancelled = true;
    };
  }, [featureLocationFilter]);

  // Options merge the endpoint list with offices already on the loaded routes, so
  // the board is never office-blind even before the endpoint exists.
  const locationOptions = useMemo(() => {
    if (!featureLocationFilter) return [];
    const byId = new Map();
    fetchedLocations.forEach((loc) => {
      if (loc.id) byId.set(String(loc.id), loc.name || String(loc.id));
    });
    deferredRoutes.forEach((route) => {
      const id = clean(route.officeId);
      if (id && !byId.has(String(id))) byId.set(String(id), clean(route.officeName) || `Office ${id}`);
    });
    return Array.from(byId.entries()).map(([id, name]) => ({ id, name }));
  }, [featureLocationFilter, fetchedLocations, deferredRoutes]);

  // The board + both map surfaces render this filtered view when a location is picked.
  const locationFilteredRoutes = useMemo(
    () =>
      selectedLocationId
        ? deferredRoutes.filter((route) => String(clean(route.officeId)) === String(selectedLocationId))
        : deferredRoutes,
    [deferredRoutes, selectedLocationId]
  );

  // Column order — persisted per tenant. Columns are per-technician, so the order
  // is keyed by technician and generalizes across days (localStorage for now).
  const columnOrderKey = useMemo(() => {
    let tid = "";
    try { tid = String(JSON.parse(localStorage.getItem("activeTenant") || "{}").id || ""); } catch { /* ignore */ }
    return `dispatch.colOrder.${tid || "x"}`;
  }, []);
  const [columnOrder, setColumnOrder] = useState(() => {
    try {
      const v = JSON.parse(localStorage.getItem(columnOrderKey) || "[]");
      return Array.isArray(v) ? v : [];
    } catch { return []; }
  });
  const techKeyOf = useCallback(
    (route) => clean(route?.technicianName) || clean(route?.technicianId) || clean(route?.id),
    []
  );
  const draggingColRef = useRef("");
  const visibleRoutes = useMemo(() => {
    if (!columnOrder.length) return locationFilteredRoutes;
    const pos = new Map(columnOrder.map((k, i) => [k, i]));
    const rank = (r) => (pos.has(techKeyOf(r)) ? pos.get(techKeyOf(r)) : Number.MAX_SAFE_INTEGER);
    // Stable sort: known techs by saved order, unknown techs keep their place after.
    return locationFilteredRoutes
      .map((r, i) => [r, i])
      .sort((a, b) => rank(a[0]) - rank(b[0]) || a[1] - b[1])
      .map(([r]) => r);
  }, [locationFilteredRoutes, columnOrder, techKeyOf]);

  const handleColumnDragStart = useCallback((event, route) => {
    draggingColRef.current = techKeyOf(route);
    try {
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", techKeyOf(route));
    } catch { /* some browsers restrict dataTransfer */ }
  }, [techKeyOf]);

  const handleColumnDrop = useCallback((targetRoute) => {
    const dragKey = draggingColRef.current;
    draggingColRef.current = "";
    const targetKey = techKeyOf(targetRoute);
    if (!dragKey || !targetKey || dragKey === targetKey) return;
    const keys = [];
    visibleRoutes.forEach((r) => {
      const k = techKeyOf(r);
      if (k && !keys.includes(k)) keys.push(k);
    });
    const from = keys.indexOf(dragKey);
    if (from < 0) return;
    keys.splice(from, 1);
    const to = keys.indexOf(targetKey);
    if (to < 0) return;
    keys.splice(to, 0, dragKey);
    setColumnOrder(keys);
    try { localStorage.setItem(columnOrderKey, JSON.stringify(keys)); } catch { /* ignore */ }
  }, [visibleRoutes, techKeyOf, columnOrderKey]);

  const scheduledAppointmentsById = useMemo(() => {
    const nextMap = new Map();
    scheduledRoutes.forEach((route) => {
      (route.stops || []).forEach((appointment) => {
        nextMap.set(clean(appointment.id), appointment);
      });
    });
    return nextMap;
  }, [scheduledRoutes]);

  const routeDayGroups = useMemo(() => {
    const groups = new Map();
    visibleRoutes.forEach((route) => {
      const serviceDay = clean(route.serviceDay || dispatchState?.day);
      if (!groups.has(serviceDay)) {
        groups.set(serviceDay, {
          day: serviceDay,
          routes: [],
        });
      }
      groups.get(serviceDay).routes.push(route);
    });
    const summaryByDay = new Map((daySummaries || []).map((summary) => [clean(summary.date), summary]));
    const orderedDays = daySummaries.length
      ? daySummaries.map((summary) => clean(summary.date))
      : Array.from(groups.keys()).sort((left, right) => left.localeCompare(right));

    // The unassigned pool is the FIRST column when it has jobs; an empty pool is
    // the "Job pool" button in the header instead of a blank column.
    const pools = scheduledRoutes.filter((route) => !clean(route.technicianId));
    return orderedDays.map((serviceDay) => {
      const dayRoutes = (groups.get(serviceDay)?.routes || []).filter((route) => clean(route.technicianId));
      const pool = pools.find((route) => clean(route.serviceDay || dispatchState?.day) === serviceDay) || pools[0] || null;
      const poolHasJobs = Boolean(pool && (pool.stops || []).length);
      return {
        day: serviceDay,
        routes: poolHasJobs ? [pool, ...dayRoutes] : dayRoutes,
        summary: summaryByDay.get(serviceDay) || null,
      };
    });
  }, [daySummaries, visibleRoutes, scheduledRoutes, dispatchState?.day]);

  const modifiedAppointments = useMemo(
    () =>
      dispatchState
        ? Object.values(dispatchState.appointmentsById).filter(isAppointmentModified)
        : [],
    [dispatchState]
  );

  const routeAlertCount = useMemo(
    () => routes.filter((route) => route.warnings.length > 0).length,
    [routes]
  );

  const selectedModifiedCount = useMemo(() => {
    const selectedSet = new Set(selectedForApplyIds.map(clean));
    return modifiedAppointments.filter((appointment) => selectedSet.has(clean(appointment.id))).length;
  }, [modifiedAppointments, selectedForApplyIds]);

  const executionSummary = executionReport?.summary || {};
  const optimizationSummary = optimizationPlan?.summary || {};
  const optimizationWindowLabel = optimizationPlan?.target_window
    ? `${optimizationPlan.target_window.start_date} → ${optimizationPlan.target_window.end_date}`
    : "Next 60 days";
  const monthlyPlanItems = useMemo(
    () => filterPlanItemsByWindow(optimizationPlan?.plan_items || [], monthWindow.targetWindow),
    [monthWindow, optimizationPlan]
  );
  const visibleRouteDeltas = useMemo(
    () =>
      (optimizationPlan?.route_deltas || []).filter((row) => {
        const serviceDay = clean(row?.service_day);
        if (!serviceDay) return false;
        if (viewMode === "day") {
          return serviceDay === clean(day);
        }
        return serviceDay >= clean(loadWindow.start_date) && serviceDay <= clean(loadWindow.end_date);
      }),
    [day, loadWindow.end_date, loadWindow.start_date, optimizationPlan, viewMode]
  );
  const auditSummary = auditReport?.summary || {};

  const boardSummary = useMemo(() => {
    const totalStops = routes.reduce((total, route) => total + route.metrics.totalStops, 0);
    const routeMinutes = routes.reduce((total, route) => total + route.metrics.routeMinutes, 0);
    const routeRevenue = routes.reduce((total, route) => total + route.metrics.routeRevenue, 0);
    // A bounded month payload holds full rows for only the first stops of each
    // day; the month's real totals come from the backend counts.
    const truncated = Boolean(boardMeta?.month_truncated);
    return {
      routes: truncated ? Number(boardMeta?.metrics?.routes || routes.length) : routes.length,
      stops: truncated ? Number(boardMeta?.metrics?.total_stops || totalStops) : totalStops,
      routeMinutes,
      routeRevenue,
      modified: modifiedAppointments.length,
      selected: selectedModifiedCount,
    };
  }, [boardMeta, modifiedAppointments.length, routes, selectedModifiedCount]);

  const selectedAppointment = useMemo(() => {
    if (!dispatchState || !selectedAppointmentId) return null;
    return dispatchState.appointmentsById[selectedAppointmentId] || null;
  }, [dispatchState, selectedAppointmentId]);
  const selectedAppointmentView = useMemo(() => {
    if (!selectedAppointment) return null;
    return scheduledAppointmentsById.get(clean(selectedAppointment.id)) || selectedAppointment;
  }, [scheduledAppointmentsById, selectedAppointment]);

  const selectedExecution = useMemo(() => {
    if (!selectedAppointment) return null;
    return (
      getExecutionKeyCandidates(selectedAppointment)
        .map((key) => executionByKey.get(key))
        .find(Boolean) || null
    );
  }, [executionByKey, selectedAppointment]);

  useEffect(() => {
    if (!selectedAppointment) {
      setDetailOpen(false);
    }
  }, [selectedAppointment]);

  const hasBoardData = Boolean(boardStateMeta);
  const syncStatusLabel =
    clean(syncMeta?.status) === "synced"
      ? "Live (FieldRoutes)"
      : clean(syncMeta?.status) === "error"
        ? "Sync Error"
        // Non-FR tenants have no sync status; the board still queries live CRM
        // appointments each load, so it is live — not a stale cached snapshot.
        : "Live (CRM)";
  const navigationLabel =
    viewMode === "month"
      ? monthWindow.label
      : viewMode === "range"
        ? `${formatNavigationDate(loadWindow.start_date)} - ${formatNavigationDate(loadWindow.end_date)}`
        : formatNavigationDate(day);
  const routeSignals = useMemo(
    () =>
      (boardMeta?.routes || []).flatMap((route) =>
        (route?.opportunities || []).map((signal, index) => ({
          id: `${clean(route?.id || route?.route_key || route?.route_id)}:${index}`,
          routeId: clean(route?.id || route?.route_key || route?.route_id),
          routeTitle: clean(route?.route_title),
          technicianName: clean(route?.technician_name) || "Unassigned",
          serviceDay: clean(route?.service_day),
          type: clean(signal?.type),
          recommendedAction: clean(signal?.recommended_action),
          reason: clean(signal?.reason),
        }))
      ),
    [boardMeta]
  );
  const jobPoolRoutes = useMemo(
    () => scheduledRoutes.filter((route) => !clean(route.technicianId)),
    [scheduledRoutes]
  );
  const poolJobCount = useMemo(() => jobPoolRoutes.reduce((n, r) => n + (r.stops || []).length, 0), [jobPoolRoutes]);
  const poolVisibleAsColumn = poolJobCount > 0;
  // What "Job pool (N)" counts when the pool column is away: unassigned work plus
  // the route signals the dispatch API surfaced.
  const jobPoolCount = poolJobCount + routeSignals.length;
  // Service families on the visible board — the legend behind the (i).
  const legendFamilies = useMemo(() => familiesOn(visibleRoutes.flatMap((r) => r.stops || [])), [visibleRoutes]);
  const mapRoutes = useMemo(
    () => (mapOpen && mapOpen !== "all" ? visibleRoutes.filter((r) => String(r.id) === String(mapOpen)) : visibleRoutes),
    [mapOpen, visibleRoutes]
  );
  const stopWord = vocab.stop || "job";
  const handleMapRoute = useCallback((route) => setMapOpen(route?.id ? String(route.id) : "all"), []);
  const handlePrint = useCallback(() => { try { window.print(); } catch { /* print blocked */ } }, []);
  useEffect(() => { setMapOpen(null); }, [day, viewMode]);

  const shiftVisibleWindow = useCallback(
    (amount) => {
      if (viewMode === "month") {
        setDay((current) => shiftIsoMonth(current, amount));
        return;
      }

      if (viewMode === "range") {
        const span = countWindowDays(rangeStart || day, rangeEnd || rangeStart || day);
        const nextStart = shiftIsoDate(rangeStart || day, amount);
        setRangeStart(nextStart);
        setRangeEnd(shiftIsoDate(nextStart, span));
        setDay(nextStart);
        return;
      }

      // Day view: the URL (?date=) is the single source of truth. Write it here
      // too — not just setDay — so the header calendar (which reads the param)
      // never diverges from the board when you page with the arrows.
      const nextDate = shiftIsoDate(day, amount);
      setDay(nextDate);
      navigate(`/schedule/day?date=${nextDate}`, { replace: true });
    },
    [day, rangeEnd, rangeStart, viewMode, navigate]
  );

  const handleJumpDate = useCallback(
    (nextDate) => {
      if (!isIsoDate(nextDate)) return;
      if (viewMode === "range") {
        const span = countWindowDays(rangeStart || day, rangeEnd || rangeStart || day);
        setRangeStart(nextDate);
        setRangeEnd(shiftIsoDate(nextDate, span));
      }
      setDay(nextDate);
    },
    [day, rangeEnd, rangeStart, viewMode]
  );

  // One destination for every date control: pick a day → that day's dispatch.
  // Keeps the URL (?date=) authoritative so the board is linkable/shareable and
  // the header calendar, arrows, and month-view day-click all agree.
  const goToDay = useCallback(
    (nextDate) => {
      if (!isIsoDate(nextDate)) return;
      handleJumpDate(nextDate);
      if (viewMode === "day") {
        navigate(`/schedule/day?date=${nextDate}`, { replace: true });
      }
    },
    [handleJumpDate, navigate, viewMode]
  );

  // Sync FROM the URL when ?date= changes under us (e.g. month-view day-click
  // routing into /schedule/day).
  useEffect(() => {
    if (viewMode !== "day") return;
    const param = new URLSearchParams(location.search).get("date");
    if (isIsoDate(param) && param !== day) setDay(param);
  }, [location.search, viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSignalSelect = useCallback((signal) => {
    const routeId = clean(signal?.routeId);
    if (!routeId) return;
    setHighlightedRouteId(routeId);
    const element = routeRefs.current.get(routeId);
    if (element?.scrollIntoView) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    if (highlightTimeoutRef.current) {
      window.clearTimeout(highlightTimeoutRef.current);
    }
    highlightTimeoutRef.current = window.setTimeout(() => {
      setHighlightedRouteId("");
      highlightTimeoutRef.current = null;
    }, 2000);
  }, []);

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        window.clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  const handleReset = useCallback(() => {
    if (!baselineState) {
      setDispatchState(null);
      return;
    }
    const visiblePlanItems = filterPlanItemsByWindow(
      optimizationPlan?.plan_items || [],
      baselineState.window || loadWindow
    );
    setDispatchState(
      appendLocalDraftRoutes(
        optimizationPlan?.plan_items?.length
          ? overlayOptimizationPlan(baselineState, visiblePlanItems)
          : baselineState,
        localRouteDrafts
      )
    );
    setExecutionReport(null);
    setSelectedForApplyIds([]);
  }, [baselineState, loadWindow, localRouteDrafts, optimizationPlan]);

  const handleSelectAppointment = useCallback((appointment) => {
    setSelectedAppointmentId(clean(appointment?.id));
  }, []);

  // Persist a single drag reassignment to the server, then RECONCILE from the
  // response — the board only ever renders what the server confirmed. Three
  // outcomes, all handled explicitly:
  //   success → refetch the board (server truth), clear the staged flag
  //   failure → snap the card back to preMoveState, toast the real error
  //   timeout → stop waiting, refetch and reconcile (never assume it landed)
  const commitReassignment = useCallback(
    async ({ movedState, preMoveState, appointmentId }) => {
      const payload = buildApplyPayload(movedState, [appointmentId]);
      if (!payload.items.length) return; // nothing actually changed
      setReassigning(true);
      setError("");
      let settled = false;
      const timeoutId = setTimeout(() => {
        if (settled) return;
        settled = true;
        toast.warn("Reassignment is taking a while — reloading the board to reconcile.");
        loadBoard().finally(() => setReassigning(false));
      }, REASSIGN_TIMEOUT_MS);
      try {
        const response = await applyOptimizationPlan(payload);
        if (settled) return; // timeout already reconciled
        settled = true;
        clearTimeout(timeoutId);
        setExecutionReport(response?.data || null);
        setSelectedForApplyIds([]);
        await loadBoard(); // render the server's truth, not the optimistic guess
        toast.success("Reassigned");
      } catch (moveError) {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        // Snap back — the server rejected the move; the board must not show it.
        setDispatchState(preMoveState);
        setSelectedForApplyIds((current) =>
          current.filter((id) => clean(id) !== clean(appointmentId))
        );
        toast.error(readApiError(moveError, "Reassignment rejected — move reverted."));
      } finally {
        if (settled) setReassigning(false);
      }
    },
    [loadBoard]
  );

  const handleDragStart = useCallback((event) => {
    setActiveAppointmentId(clean(event?.active?.id));
  }, []);

  const handleDragEnd = useCallback(
    (event) => {
      setActiveAppointmentId("");
      if (!dispatchState) return;

      const activeId = clean(event?.active?.id);
      const activeRouteId = clean(event?.active?.data?.current?.routeId);
      const over = event?.over;
      if (!activeId || !activeRouteId || !over) return;

      const overType = clean(over?.data?.current?.type);
      const targetRouteId =
        clean(over?.data?.current?.routeId) ||
        (overType === "route" ? clean(over?.id) : "");
      if (!targetRouteId) return;

      const targetRoute = dispatchState.routesById[targetRouteId];
      if (!targetRoute) return;

      const overAppointmentId = overType === "appointment" ? clean(over.id) : "";
      const overIndex = overAppointmentId
        ? targetRoute.stopIds.indexOf(overAppointmentId)
        : targetRoute.stopIds.length;

      const preMoveState = dispatchState;
      const movedState = moveAppointment(preMoveState, {
        appointmentId: activeId,
        sourceRouteId: activeRouteId,
        targetRouteId,
        targetIndex: overIndex,
      });
      // A drop that changed nothing (same slot) — no stage, no server call.
      const movedAppt = movedState.appointmentsById?.[activeId];
      if (movedAppt && !isAppointmentModified(movedAppt)) { setSelectedAppointmentId(activeId); return; }

      // FieldRoutes owns this tenant's routes until route-assignment writeback is
      // switched on: refuse the move and say so, instead of a staged change that
      // Apply would then reject.
      if (isFieldRoutesTenant && writeback?.known && !writeback.routeAssignment) {
        setSelectedAppointmentId(activeId);
        toast.info(`Managed in FieldRoutes — ${vocab.route} changes are made there until route-assignment writeback is switched on for this account.`);
        return;
      }
      setDispatchState(movedState);
      setSelectedAppointmentId(activeId);

      if (isFieldRoutesTenant) {
        // FR: staged preview only. FR owns writes for FR tenants (route-assignment
        // writeback is default-denied), so the move commits through Apply, not per drag.
        setSelectedForApplyIds((current) =>
          current.includes(activeId) ? current : [...current, activeId]
        );
        return;
      }
      // Native tenant owns its appointments — commit this move now, with snap-back.
      commitReassignment({ movedState, preMoveState, appointmentId: activeId });
    },
    [dispatchState, isFieldRoutesTenant, commitReassignment, writeback, vocab]
  );

  const handleToggleApplySelection = useCallback((appointmentId) => {
    const normalizedId = clean(appointmentId);
    if (!normalizedId) return;
    setSelectedForApplyIds((current) =>
      current.includes(normalizedId)
        ? current.filter((value) => value !== normalizedId)
        : [...current, normalizedId]
    );
  }, []);

  useEffect(() => {
    if (!dispatchState) {
      setSelectedForApplyIds([]);
      return;
    }
    const modifiedIds = new Set(
      Object.values(dispatchState.appointmentsById)
        .filter(isAppointmentModified)
        .map((appointment) => clean(appointment.id))
    );
    setSelectedForApplyIds((current) => current.filter((id) => modifiedIds.has(clean(id))));
  }, [dispatchState]);

  const handleApply = useCallback(async () => {
    if (!dispatchState) return;
    const payload = buildApplyPayload(dispatchState, selectedForApplyIds);
    if (!payload.items.length) return;

    setApplyLoading(true);
    setError("");
    try {
      const response = await applyOptimizationPlan(payload);
      setExecutionReport(response?.data || null);
      setAuditReport(null);
      setSelectedForApplyIds([]);
      if (optimizationPlan) setOptimizationPlan(null);
      await loadBoard();
    } catch (applyError) {
      setError(readApiError(applyError, "Unable to apply dispatch changes."));
    } finally {
      setApplyLoading(false);
    }
  }, [dispatchState, loadBoard, optimizationPlan, selectedForApplyIds]);

  const handleOptimize = useCallback(async () => {
    setOptimizationLoading(true);
    setError("");
    try {
      const response = await optimizeWithConstraints({
        mode: "full",
        constraints: buildConstraintPayload(constraints),
      });
      setOptimizationPlan(response?.data || null);
      setExecutionReport(null);
      setAuditReport(null);
      setSelectedForApplyIds([]);
    } catch (nextError) {
      setError(readApiError(nextError, "Unable to build an optimization preview."));
    } finally {
      setOptimizationLoading(false);
    }
  }, [constraints]);

  const handleClearPreview = useCallback(() => {
    setOptimizationPlan(null);
    setAuditReport(null);
    setSelectedForApplyIds([]);
  }, []);

  const activeAppointment = useMemo(() => {
    if (!dispatchState || !activeAppointmentId) return null;
    return dispatchState.appointmentsById[activeAppointmentId] || null;
  }, [activeAppointmentId, dispatchState]);
  const activeAppointmentView = useMemo(() => {
    if (!activeAppointment) return null;
    return scheduledAppointmentsById.get(clean(activeAppointment.id)) || activeAppointment;
  }, [activeAppointment, scheduledAppointmentsById]);

  return (
    <div className="dispatch-shell">
      {/* ONE compact row (FR-style): ← [date ▾] →  ·  N routes · N stops · Nh · $X
          with everything secondary folded into an overflow menu. */}
      <header className="dispatch-topbar dispatch-topbar--compact">
        <div className="dispatch-date-nav" aria-label="Schedule date navigation">
          <button
            type="button"
            className="dispatch-date-nav-btn"
            onClick={() => shiftVisibleWindow(-1)}
            aria-label="Previous schedule window"
          >
            &larr;
          </button>
          {viewMode === "day" ? (
            <CalendarDropdown value={day} onPick={goToDay} />
          ) : (
            <span className="dispatch-date-display is-static">{navigationLabel}</span>
          )}
          <button
            type="button"
            className="dispatch-date-nav-btn"
            onClick={() => shiftVisibleWindow(1)}
            aria-label="Next schedule window"
          >
            &rarr;
          </button>
          {/* (i) — the service-family legend, behind one icon next to the date. */}
          {hasBoardData && legendFamilies.length ? (
            <span className="dispatch-legend-wrap">
              <button
                type="button"
                className="dispatch-date-nav-btn dispatch-legend-btn"
                onClick={() => setLegendOpen((v) => !v)}
                aria-expanded={legendOpen}
                aria-label="Service colour legend"
                title="What the colours mean"
              >
                <Info size={14} />
              </button>
              {legendOpen ? (
                <>
                  <button type="button" className="dispatch-overflow-backdrop" aria-hidden="true" tabIndex={-1} onClick={() => setLegendOpen(false)} />
                  <div className="dispatch-legend-pop" role="dialog" aria-label="Service colour legend">
                    <div className="dispatch-legend-title">Left edge = {vocab.service} family</div>
                    {legendFamilies.map((f) => (
                      <div key={f.key} className="dispatch-legend-row"><i style={{ "--pin": f.color }} /><span>{f.label}</span><b>{f.count}</b></div>
                    ))}
                  </div>
                </>
              ) : null}
            </span>
          ) : null}
        </div>

        {featureLocationFilter ? (
          <LocationFilter
            locations={locationOptions}
            value={selectedLocationId}
            onChange={setSelectedLocationId}
          />
        ) : null}

        {hasBoardData ? (
          <div className="dispatch-topbar-totals" aria-label="Board totals">
            <span>{boardSummary.routes} {vocab.routes}</span>
            <span className="dispatch-summary-sep">·</span>
            <span>{boardSummary.stops} {vocab.stops}</span>
            <span className="dispatch-summary-sep">·</span>
            <span>{formatHours(boardSummary.routeMinutes)}</span>
            <span className="dispatch-summary-sep">·</span>
            <span className="dispatch-topbar-total-value">{formatCurrency(boardSummary.routeRevenue)}</span>
          </div>
        ) : null}

        {reassigning ? (
          <span className="dispatch-saving" role="status" aria-live="polite">
            <RotateCcw size={12} className="dispatch-saving-spin" />
            Saving…
          </span>
        ) : null}

        <div className="dispatch-actions" aria-label="Primary dispatch actions">
          {isFieldRoutesTenant ? (
            <>
              <button
                type="button"
                className="dispatch-button secondary"
                onClick={handleOptimize}
                disabled={optimizationLoading || loading}
              >
                <Sparkles size={14} />
                {optimizationLoading ? "Optimizing…" : "Optimize"}
              </button>
              <button
                type="button"
                className={`dispatch-button secondary ${constraintsOpen ? "is-active" : ""}`}
                onClick={() => setConstraintsOpen(true)}
              >
                <SlidersHorizontal size={14} />
                Constraints
              </button>
              {selectedModifiedCount ? (
                <button
                  type="button"
                  className="dispatch-button primary"
                  onClick={handleApply}
                  disabled={applyLoading}
                >
                  <Send size={14} />
                  {applyLoading ? "Applying…" : `Apply (${selectedModifiedCount})`}
                </button>
              ) : null}
            </>
          ) : null}

          {/* The pool column is away when it has no jobs; the button stands in
              for it only while there is something to open (unassigned work or
              route signals). An empty pool shows nothing. */}
          {hasBoardData && !poolVisibleAsColumn && jobPoolCount > 0 ? (
            <button
              type="button"
              className={`dispatch-button secondary ${showJobPool ? "is-active" : ""}`}
              onClick={() => setShowJobPool((v) => !v)}
              aria-pressed={showJobPool}
              title={`Unassigned ${vocab.stops} and route signals`}
            >
              Job pool ({jobPoolCount})
            </button>
          ) : null}

          {hasBoardData ? (
            <button
              type="button"
              className={`dispatch-button secondary ${mapOpen ? "is-active" : ""}`}
              onClick={() => setMapOpen((v) => (v ? null : "all"))}
              aria-pressed={Boolean(mapOpen)}
            >
              <MapIcon size={14} />
              Day map
            </button>
          ) : null}

          <button type="button" className="dispatch-button secondary" onClick={handlePrint}>
            <Printer size={14} />
            Print
          </button>

          <div className="dispatch-overflow">
            <button
              type="button"
              className="dispatch-date-nav-btn dispatch-overflow-trigger"
              onClick={() => setOverflowOpen((v) => !v)}
              aria-expanded={overflowOpen}
              aria-label="More dispatch options"
            >
              <MoreHorizontal size={16} />
            </button>
            {overflowOpen ? (
              <>
                <button
                  type="button"
                  className="dispatch-overflow-backdrop"
                  aria-hidden="true"
                  tabIndex={-1}
                  onClick={() => setOverflowOpen(false)}
                />
                <div className="dispatch-overflow-menu" role="menu">
                  {/* Board layout — one board, three presentations. The tenant
                      vertical picks the default; the choice persists per user. */}
                  <div className="dispatch-overflow-label">Layout</div>
                  {BOARD_PRESETS.map((p) => (
                    <button
                      key={p.key}
                      type="button"
                      role="menuitemradio"
                      aria-checked={boardPreset === p.key}
                      className={`dispatch-overflow-item ${boardPreset === p.key ? "is-active" : ""}`}
                      onClick={() => { setBoardPreset(p.key); setOverflowOpen(false); }}
                      title={p.title}
                    >
                      {p.label} {boardPreset === p.key ? "✓" : ""}
                    </button>
                  ))}
                  {isFieldRoutesTenant ? (
                    <button
                      type="button"
                      className={`dispatch-overflow-item ${showReminders ? "is-active" : ""}`}
                      onClick={() => setShowReminders((v) => !v)}
                    >
                      <Bell size={14} /> Reminders {showReminders ? "✓" : ""}
                    </button>
                  ) : null}
                  {isFieldRoutesTenant && selectedAppointment ? (
                    <button
                      type="button"
                      className="dispatch-overflow-item"
                      onClick={() => {
                        setDetailOpen(true);
                        setOverflowOpen(false);
                      }}
                    >
                      <FileText size={14} /> Inspect
                    </button>
                  ) : null}
                  {optimizationPlan ? (
                    <button
                      type="button"
                      className="dispatch-overflow-item"
                      onClick={() => {
                        handleClearPreview();
                        setOverflowOpen(false);
                      }}
                    >
                      <RotateCcw size={14} /> Clear Preview
                    </button>
                  ) : null}
                  {modifiedAppointments.length ? (
                    <button
                      type="button"
                      className="dispatch-overflow-item"
                      onClick={() => {
                        handleReset();
                        setOverflowOpen(false);
                      }}
                    >
                      <RefreshCw size={14} /> Reset
                    </button>
                  ) : null}
                </div>
              </>
            ) : null}
          </div>
        </div>

        {/* Sync age — small muted text at the far right; red past 24 h. */}
        {hasBoardData ? (
          <div className="dispatch-topbar-sync">
            {isFieldRoutesTenant || boardMeta?.day_sync?.is_fieldroutes ? (
              <FreshnessChip board={boardMeta} day={day} onRefreshed={() => loadBoard({ refresh: true })} onState={setFreshState} />
            ) : <span className="dfresh dfresh--live">Live</span>}
          </div>
        ) : null}
      </header>

      {showStaleBanner ? (
        <div className="dispatch-stale-banner" role="alert">
          <AlertTriangle size={18} className="dispatch-stale-banner-icon" />
          <div className="dispatch-stale-banner-copy">
            <strong>{freshState?.failed ? "The FieldRoutes refresh failed." : `This day has never been synced from FieldRoutes.`}</strong>
            <span>{freshState?.failed ? `Showing the last synced ${vocab.routes}. Try again, or check the FieldRoutes connection in Settings.` : `The ${vocab.routes} below may be missing — refresh to pull the day.`}</span>
          </div>
          <button
            type="button"
            className="dispatch-button primary dispatch-stale-banner-btn"
            onClick={() => loadBoard({ refresh: true })}
            disabled={loading}
          >
            <RefreshCw size={14} />
            {loading ? "Refreshing…" : "Refresh now"}
          </button>
        </div>
      ) : null}

      {showReminders ? (
        <div className="dispatch-inline-status">
          Reminder automation remains backend-driven. The dispatch board reflects route state and
          changes, but reminder sends and suppression continue through the existing policy flow.
        </div>
      ) : null}

      {loading && !hasBoardData ? (
        <section className="dispatch-state-panel" aria-live="polite">
          <strong>Loading dispatch window…</strong>
          <p>Fetching the snapshot-backed routes for the selected window.</p>
        </section>
      ) : null}

      {error && !hasBoardData ? (
        <section className="dispatch-state-panel is-error" aria-live="assertive">
          <strong>Unable to load the dispatch board</strong>
          <p>{error}</p>
          <div className="dispatch-state-panel-actions">
            <button
              type="button"
              className="dispatch-button secondary"
              onClick={() => loadBoard({ refresh: true })}
            >
              <RefreshCw size={14} />
              Retry Live Refresh
            </button>
          </div>
        </section>
      ) : null}

      {/* Totals moved inline into the compact header row (dispatch-topbar-totals). */}

      {hasBoardData && !boardSummary.routes && clean(syncMeta?.status) === "synced" ? (
        <div className="dispatch-inline-status">
          No routes returned for this window. This may indicate no scheduled work.
        </div>
      ) : null}

      {optimizationPlan ? (
        <section className="dispatch-plan-summary">
          <div className="dispatch-plan-summary-head">
            <div>
              <div className="dispatch-header-kicker">Optimizer Preview</div>
              <h3>
                {optimizationWindowLabel}
              </h3>
              <p>
                Preview generated by the constrained optimizer. Baseline routes stay visible for
                the loaded window, and preview changes are projected on top without replacing the
                underlying schedule.
              </p>
            </div>
            <div className="dispatch-plan-summary-badges">
              <span className="dispatch-result-chip">Moves {optimizationSummary.moves || 0}</span>
              <span className="dispatch-result-chip">
                Reassignments {optimizationSummary.reassignments || 0}
              </span>
              <span className="dispatch-result-chip is-skipped">
                Prevented {optimizationSummary.violations_prevented || 0}
              </span>
              <span className="dispatch-result-chip">
                Month items {monthlyPlanItems.length}
              </span>
            </div>
          </div>

          <div className="dispatch-plan-delta-grid">
            {visibleRouteDeltas.length ? (
              visibleRouteDeltas.map((row) => (
                <div
                  key={`${row.route_key}-${row.service_day}`}
                  className="dispatch-plan-delta-card"
                >
                  <span>{row.technician_name || "Route"}</span>
                  <strong>
                    {row.appointments_delta > 0 ? "+" : ""}
                    {row.appointments_delta} appointments
                  </strong>
                  <div>
                    {row.drive_minutes_delta > 0 ? "+" : ""}
                    {row.drive_minutes_delta} drive min
                  </div>
                  <div>
                    {row.distance_miles_delta > 0 ? "+" : row.distance_miles_delta < 0 ? "-" : ""}
                    {Math.abs(row.distance_miles_delta || 0).toFixed(1)} mi
                  </div>
                  <div>
                    {row.value_delta > 0 ? "+" : row.value_delta < 0 ? "-" : ""}
                    {formatCurrency(Math.abs(row.value_delta || 0))}
                    {row.value_delta < 0 ? " down" : " up"}
                  </div>
                  <div>{row.proposed_due_date_deviation_days || 0} due-day deviation</div>
                </div>
              ))
            ) : (
              <div className="dispatch-inline-status">
                No optimizer deltas land in the loaded window. Change the window controls to inspect
                another route slice from the active preview.
              </div>
            )}
          </div>
        </section>
      ) : null}

      {error && hasBoardData ? <div className="dispatch-inline-status error">{error}</div> : null}
      {loading && hasBoardData ? <div className="dispatch-inline-status">Loading dispatch board…</div> : null}

      {executionReport ? (
        <section className="dispatch-result-bar">
          <div className="dispatch-result-chip is-applied">Applied {executionSummary.applied || 0}</div>
          <div className="dispatch-result-chip is-skipped">
            Skipped {(executionSummary.skipped || 0) + (executionSummary.blocked || 0)}
          </div>
          <div className="dispatch-result-chip is-failed">
            Failed {(executionSummary.failed || 0) + (executionSummary.partial_sync || 0) + (executionSummary.drift_detected || 0)}
          </div>
        </section>
      ) : null}

      {auditReport ? (
        <section className="dispatch-audit-bar">
          <div className="dispatch-result-chip">
            Audit total {auditSummary.total || 0}
          </div>
          <div className="dispatch-result-chip is-failed">
            Critical {auditSummary.critical || 0}
          </div>
          <div className="dispatch-result-chip is-skipped">
            Medium {auditSummary.medium || 0}
          </div>
          <div className="dispatch-result-chip">
            Minor {(auditSummary.high || 0) + (auditSummary.low || 0)}
          </div>
        </section>
      ) : null}

      {hasBoardData ? (
        <div className="dispatch-layout">
          <div className="dispatch-main">
            {/* The board is the page. "Day map" puts the map in its place,
                full-width, with a Back — the Maps script loads only then. */}
            {mapOpen ? (
              <section className="dispatch-daymap" aria-label="Day map">
                <div className="dispatch-daymap-head">
                  <button type="button" className="dispatch-button secondary" onClick={() => setMapOpen(null)}>
                    <ArrowLeft size={14} />
                    Back
                  </button>
                  <span className="dispatch-daymap-title">
                    {mapOpen !== "all" && mapRoutes[0]
                      ? `${mapRoutes[0].technicianName === "Unassigned" ? vocab.unassigned : mapRoutes[0].technicianName} · ${(mapRoutes[0].stops || []).length} ${vocab.stops}`
                      : `${boardSummary.routes} ${vocab.routes} · ${boardSummary.stops} ${vocab.stops}`}
                  </span>
                  {mapOpen !== "all" ? (
                    <button type="button" className="dispatch-button secondary" onClick={() => setMapOpen("all")}>All {vocab.routes}</button>
                  ) : null}
                </div>
                <Suspense fallback={<div className="dispatch-daymap-loading">Loading the map…</div>}>
                  <DispatchMap
                    routes={mapRoutes}
                    day={day}
                    legsLive={Boolean(boardMeta?.day_sync)}
                    selectedAppointmentId={selectedAppointmentId}
                    onSelectAppointment={handleSelectAppointment}
                    onOpenCustomer={(stop) => {
                      if (stop?.customerId) { setProfileStop(stop); return; }
                      const id = stop?.crmAppointmentId || stop?.id;
                      if (id) navigate(`/schedule/appointment/${id}`, { state: { boardStartLabel: stop?.computedStart || null } });
                    }}
                    vocab={vocab}
                  />
                </Suspense>
              </section>
            ) : (
            <div className="dispatch-board-col">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <div className="dispatch-route-day-groups">
                {!routeDayGroups.length && !loading ? (
                  <div className="dispatch-inline-status">
                    No routed appointments were returned for this window. The baseline schedule is still authoritative; widen the date range or run a live refresh if you expect FieldRoutes work here.
                  </div>
                ) : null}
                {routeDayGroups.map((group) => (
                  <section key={group.day} className="dispatch-route-day-section">
                    {/* Multi-day windows keep a slim day divider; the single-day
                        board needs none — the header date control names the day. */}
                    {routeDayGroups.length > 1 ? (
                      <header className="dispatch-route-day-head dispatch-route-day-head--compact">
                        <h4>{formatDayLabel(group.day)}</h4>
                        {group.summary?.more_count > 0 ? (
                          /* Month view carries full rows for only the first few
                             stops of each day; the counts are the day's real totals. */
                          <span className="dispatch-route-day-meta">
                            {group.summary.route_count} routes · showing {group.summary.shown_count} of {group.summary.stop_count} stops ·{" "}
                            <button
                              type="button"
                              className="dispatch-link-btn"
                              onClick={() => navigate(`/schedule/day?date=${group.day}`)}
                            >
                              Open day ({group.summary.more_count} more)
                            </button>
                          </span>
                        ) : (
                          <span className="dispatch-route-day-meta">
                            {group.routes.length} routes · {group.routes.reduce((n, r) => n + (r.metrics?.totalStops || 0), 0)} stops
                          </span>
                        )}
                      </header>
                    ) : null}
                    {/* Phone: one route per screen, with a switcher. */}
                    {isPhone && group.routes.length > 1 ? (() => {
                      const picked = group.routes.findIndex((r) => String(r.id) === String(phoneRouteId));
                      const i = picked >= 0 ? picked : Math.max(0, group.routes.findIndex((r) => (r.stops || []).length > 0));
                      const go = (n) => setPhoneRouteId(String(group.routes[(n + group.routes.length) % group.routes.length].id));
                      return (
                        <div className="sc-route-switch">
                          <button type="button" className="dispatch-date-nav-btn" onClick={() => go(i - 1)} aria-label={`Previous ${vocab.route}`}>&larr;</button>
                          <select className="sc-select" value={String(group.routes[i].id)} onChange={(e) => setPhoneRouteId(e.target.value)} aria-label={`Choose a ${vocab.route}`}>
                            {group.routes.map((r) => (
                              <option key={r.id} value={String(r.id)}>{r.technicianName === "Unassigned" ? vocab.unassigned : r.technicianName} · {r.metrics?.totalStops || 0} {vocab.stops}</option>
                            ))}
                          </select>
                          <button type="button" className="dispatch-date-nav-btn" onClick={() => go(i + 1)} aria-label={`Next ${vocab.route}`}>&rarr;</button>
                        </div>
                      );
                    })() : null}
                    {(
                      <div className="dispatch-route-board">
                        {(isPhone && group.routes.length > 1
                          ? [group.routes.find((r) => String(r.id) === String(phoneRouteId)) || group.routes.find((r) => (r.stops || []).length > 0) || group.routes[0]]
                          : group.routes
                        ).map((route) => (
                          <div
                            key={route.id}
                            ref={(node) => {
                              if (node) {
                                routeRefs.current.set(route.id, node);
                              } else {
                                routeRefs.current.delete(route.id);
                              }
                            }}
                            className={`dispatch-route-wrap${highlightedRouteId === route.id ? " dispatch-route-highlight" : ""}`}
                            onDragOver={(event) => {
                              if (draggingColRef.current) event.preventDefault();
                            }}
                            onDrop={(event) => {
                              if (!draggingColRef.current) return;
                              event.preventDefault();
                              handleColumnDrop(route);
                            }}
                          >
                            {/* Drag this strip to reorder columns (persists per tenant).
                                Separate from the pill drag (dnd-kit) below it. */}
                            <div
                              className="dispatch-col-draghandle"
                              draggable
                              title="Drag to reorder this column"
                              aria-label={`Reorder ${route.technicianName || "route"} column`}
                              onDragStart={(event) => handleColumnDragStart(event, route)}
                              onDragEnd={() => { draggingColRef.current = ""; }}
                            >
                              <GripHorizontal size={12} />
                            </div>
                            <RouteColumn
                              route={route.technicianName === "Unassigned" ? { ...route, technicianName: vocab.unassigned } : route}
                              preset={boardPreset}
                              selectedAppointmentId={selectedAppointmentId}
                              selectedForApplyIds={selectedForApplyIds}
                              executionByKey={executionByKey}
                              onSelectAppointment={handleSelectAppointment}
                              onToggleApplySelection={handleToggleApplySelection}
                              showReminders={showReminders}
                              onQuickAdd={handleQuickAdd}
                              onMapRoute={handleMapRoute}
                              stopWord={stopWord}
                              managedNote={isFieldRoutesTenant ? `${capitalize(vocab.worker)}, buffer and who-can-schedule are managed in FieldRoutes for this ${vocab.route}.` : ""}
                            />
                          </div>
                        ))}
                        {!group.routes.length ? (
                          <div className="dispatch-route-day-empty">
                            No routes scheduled for this day in the current snapshot window.
                          </div>
                        ) : null}
                      </div>
                    )}
                  </section>
                ))}
              </div>

              <DragOverlay>
                {activeAppointmentView ? (
                  <div className="dispatch-pill dispatch-pill-overlay">
                    <div className="dispatch-pill-row">
                      <span className="dispatch-pill-time">
                        {activeAppointmentView.computedStart || formatTime(activeAppointmentView.proposedStart)}
                      </span>
                      <span className="dispatch-pill-copy">
                        <strong>{activeAppointmentView.lastName}</strong>
                      </span>
                      <span className="dispatch-pill-icons">
                        <StatusIndicator kind="execution" value="modified" label="Moving" />
                      </span>
                    </div>
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
            </div>
            )}

            {showJobPool ? (
              <section className="dispatch-signal-panel">
                <header className="dispatch-panel-head">
                  <div>
                    <h3>Job Pool</h3>
                    <p>Unassigned work and route signals surfaced from the existing dispatch API.</p>
                  </div>
                </header>
                <div className="dispatch-signal-list">
                  {jobPoolRoutes.length ? (
                    jobPoolRoutes.map((route) => (
                      <button
                        key={`job-pool-${route.id}`}
                        type="button"
                        className="dispatch-signal-card"
                        onClick={() =>
                          handleSignalSelect({
                            routeId: route.id,
                          })
                        }
                      >
                        <div className="dispatch-signal-head">
                          <strong>{route.routeTitle || "Unassigned"}</strong>
                          <span>{route.serviceDay || "Open window"}</span>
                        </div>
                        <div className="dispatch-signal-type">{route.metrics.totalStops} unassigned stops</div>
                        <div className="dispatch-signal-meta">Move work into a staffed route or add capacity.</div>
                      </button>
                    ))
                  ) : routeSignals.length ? (
                    routeSignals.slice(0, 24).map((signal) => (
                      <button
                        key={signal.id}
                        type="button"
                        className="dispatch-signal-card"
                        onClick={() => handleSignalSelect(signal)}
                      >
                        <div className="dispatch-signal-head">
                          <strong>{signal.technicianName}</strong>
                          <span>{signal.serviceDay || "Open window"}</span>
                        </div>
                        <div className="dispatch-signal-type">
                          {signal.type || signal.recommendedAction || "Opportunity"}
                        </div>
                        {signal.routeTitle ? (
                          <div className="dispatch-signal-meta">{signal.routeTitle}</div>
                        ) : null}
                        {signal.reason ? <div className="dispatch-signal-meta">{signal.reason}</div> : null}
                      </button>
                    ))
                  ) : (
                    <div className="dispatch-detail-empty">
                      No job pool items or route signals were returned for this window.
                    </div>
                  )}
                </div>
              </section>
            ) : null}
          </div>
        </div>
      ) : null}

      {constraintsOpen ? (
        <div className="dispatch-modal" role="dialog" aria-modal="true" aria-label="Constraint panel">
          <div className="dispatch-modal-card">
            <header className="dispatch-modal-head">
              <div>
                <div className="dispatch-header-kicker">Optimizer Controls</div>
                <h3>Constraint Panel</h3>
              </div>
              <button
                type="button"
                className="dispatch-button secondary"
                onClick={() => setConstraintsOpen(false)}
                aria-label="Close constraint panel"
              >
                Close
              </button>
            </header>
            <div className="dispatch-modal-body">
              <ConstraintPanel
                constraints={constraints}
                onChange={setConstraints}
                modifiedCount={modifiedAppointments.length}
                routeAlertCount={routeAlertCount}
                optimizationWindowLabel={optimizationWindowLabel}
              />
            </div>
          </div>
          <button
            type="button"
            className="dispatch-modal-backdrop"
            onClick={() => setConstraintsOpen(false)}
            aria-label="Close constraint panel"
          />
        </div>
      ) : null}

      {detailOpen ? (
        <div className="dispatch-modal" role="dialog" aria-modal="true" aria-label="Appointment detail">
          <div className="dispatch-modal-card">
            <header className="dispatch-modal-head">
              <div>
                <div className="dispatch-header-kicker">Appointment Detail</div>
                <h3>{selectedAppointmentView?.customerName || "Selected appointment"}</h3>
              </div>
              <button
                type="button"
                className="dispatch-button secondary"
                onClick={() => setDetailOpen(false)}
                aria-label="Close appointment detail"
              >
                Close
              </button>
            </header>
            <div className="dispatch-modal-body">
              {selectedAppointmentView ? (
                <div className="dispatch-detail-body">
                  <div className="dispatch-detail-title-row">
                    <strong>{selectedAppointmentView.customerName}</strong>
                    <div className="dispatch-detail-title-tags">
                      <StatusIndicator
                        kind="execution"
                        value={getVisualState(selectedAppointment, selectedExecution)}
                        label={getVisualState(selectedAppointment, selectedExecution)}
                        showLabel
                      />
                      <StatusIndicator
                        kind="appointment"
                        value={selectedAppointmentView.status}
                        label={selectedAppointmentView.status || "scheduled"}
                        showLabel
                      />
                    </div>
                  </div>
                  <div className="dispatch-detail-grid">
                    <div>
                      <span>Service</span>
                      <strong>{selectedAppointmentView.serviceName}</strong>
                    </div>
                    <div>
                      <span>Value</span>
                      <strong>{formatCurrency(selectedAppointmentView.value)}</strong>
                    </div>
                    <div>
                      <span>Current Window</span>
                      <strong>
                        {selectedAppointmentView.computedStart || formatTime(selectedAppointmentView.currentStart)} -{" "}
                        {selectedAppointmentView.computedEnd || formatTime(selectedAppointmentView.currentEnd)}
                      </strong>
                    </div>
                    <div>
                      <span>Proposed Window</span>
                      <strong>
                        {selectedAppointmentView.computedStart || formatTime(selectedAppointmentView.proposedStart)} -{" "}
                        {selectedAppointmentView.computedEnd || formatTime(selectedAppointmentView.proposedEnd)}
                      </strong>
                    </div>
                    <div>
                      <span>Due Deviation</span>
                      <strong>{`\u00b1${selectedAppointmentView.dueDateDeviationDays || 0} days`}</strong>
                    </div>
                    <div>
                      <span>Queued For Apply</span>
                      <strong>{selectedForApplyIds.includes(clean(selectedAppointmentView.id)) ? "Yes" : "No"}</strong>
                    </div>
                    <div>
                      <span>Classification</span>
                      <strong>{selectedAppointmentView.classification || "FLEXIBLE"}</strong>
                    </div>
                    <div>
                      <span>Service Day</span>
                      <strong>{selectedAppointmentView.serviceDay || "—"}</strong>
                    </div>
                    <div>
                      <span>Current {staffLabel}</span>
                      <strong>{selectedAppointmentView.currentTechnicianName || "Unassigned"}</strong>
                    </div>
                    <div>
                      <span>Proposed {staffLabel}</span>
                      <strong>{selectedAppointmentView.proposedTechnicianName || "Unassigned"}</strong>
                    </div>
                  </div>

                  {selectedAppointmentView.address ? (
                    <div className="dispatch-detail-section">
                      <span>Address</span>
                      <strong>{selectedAppointmentView.address}</strong>
                    </div>
                  ) : null}

                  {selectedAppointmentView.notes ? (
                    <div className="dispatch-detail-section">
                      <span>Notes</span>
                      <strong>{selectedAppointmentView.notes}</strong>
                    </div>
                  ) : null}

                  {selectedAppointmentView.optimizationReason ? (
                    <div className="dispatch-detail-result">
                      <div className="dispatch-detail-section">
                        <span>Optimizer Reason</span>
                        <strong>{selectedAppointmentView.optimizationReason}</strong>
                      </div>
                      {selectedAppointmentView.optimizationReasonCodes?.length ? (
                        <div className="dispatch-detail-tags">
                          {selectedAppointmentView.optimizationReasonCodes.map((reason) => (
                            <span key={`${selectedAppointmentView.id}-opt-${reason}`} className="dispatch-detail-tag">
                              {reason.replace(/_/g, " ")}
                            </span>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  {selectedExecution ? (
                    <div className="dispatch-detail-result">
                      <div className="dispatch-detail-section">
                        <span>Execution Outcome</span>
                        <strong>{selectedExecution.outcome}</strong>
                      </div>
                      {selectedExecution.summary ? (
                        <div className="dispatch-detail-section">
                          <span>Summary</span>
                          <strong>{selectedExecution.summary}</strong>
                        </div>
                      ) : null}
                      {selectedExecution.reason_codes?.length ? (
                        <div className="dispatch-detail-tags">
                          {selectedExecution.reason_codes.map((reason) => (
                            <span key={`${selectedAppointment.id}-${reason}`} className="dispatch-detail-tag">
                              {reason.replace(/_/g, " ")}
                            </span>
                          ))}
                        </div>
                      ) : null}
                      {selectedExecution.actual_final_abon_state?.start ? (
                        <div className="dispatch-detail-section">
                          <span>Abon Final</span>
                          <strong>{formatDateTime(selectedExecution.actual_final_abon_state.start)}</strong>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="dispatch-detail-empty">Select an appointment to inspect it.</div>
              )}
            </div>
          </div>
          <button
            type="button"
            className="dispatch-modal-backdrop"
            onClick={() => setDetailOpen(false)}
            aria-label="Close appointment detail"
          />
        </div>
      ) : null}

      <QuickAddSheet
        open={Boolean(quickAdd)}
        onClose={() => setQuickAdd(null)}
        defaultDate={day}
        defaultTime={quickAdd?.time || "09:00"}
        assigneeUserId={quickAdd?.assigneeUserId || null}
        assigneeName={quickAdd?.assigneeName || ""}
        onCreated={() => loadBoard({ refresh: true })}
      />

      {profileStop ? (
        <Suspense fallback={null}>
          <CustomerPopup
            lead={{ object: "customer", id: profileStop.customerId, customer_id: profileStop.customerId, display_name: profileStop.customerName }}
            leadType="customer"
            initialTab="appointments"
            focusAppointment={profileStop.crmAppointmentId || profileStop.id}
            onClose={() => setProfileStop(null)}
          />
        </Suspense>
      ) : null}

    </div>
  );
}
